import { Link } from "react-router-dom";
import { LOGO_URL, WHATSAPP_URL } from "@/lib/brand";
import { Monitor, Shield, MapPin, User, Brain, BookOpen, PlayCircle, ArrowUpRight } from "lucide-react";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center px-6 md:px-14 pt-12 pb-16">
          <div className="max-w-xl">
            <h1
              data-testid="hero-title"
              className="rise rise-1"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 500,
                fontSize: "clamp(2.4rem, 5vw, 4rem)",
                lineHeight: 1.05,
                color: "var(--p-black)",
                letterSpacing: "-0.01em",
              }}
            >
              Equilibrio para tu mente,<br />
              bienestar para tu vida.
            </h1>
            <p
              className="mt-6 text-lg leading-relaxed rise rise-2"
              style={{ color: "var(--p-brown)", fontFamily: "Cormorant Garamond, serif" }}
            >
              Para ayudarte a construir una vida con mayor<br className="hidden md:block" />
              equilibrio, bienestar y salud emocional.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 rise rise-3">
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="hero-cta-agendar" className="btn-primary">
                Agenda tu primera sesión
              </a>
              <Link to="/servicios" data-testid="hero-cta-servicios" className="btn-ghost">
                Conoce nuestros servicios
              </Link>
            </div>

            {/* micro-badges */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 rise rise-4">
              <MicroBadge Icon={Monitor} title="Atención 100 % online" subtitle="desde cualquier lugar" />
              <MicroBadge Icon={Shield} title="Sesiones confidenciales" subtitle="y seguras" />
              <MicroBadge Icon={MapPin} title="Te acompañamos" subtitle="desde donde estés" />
            </div>
          </div>

          {/* Right image collage */}
          <div className="relative rise rise-2">
            <div className="relative aspect-[5/4] rounded-sm overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&w=1400&q=80"
                alt="Espacio terapéutico"
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(74,21,33,0.05), rgba(245,239,230,0.05))",
                }}
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 px-5 py-4 rounded-sm" style={{ background: "var(--p-cream)", border: "1px solid rgba(138,116,101,0.25)", boxShadow: "0 20px 40px -30px rgba(0,0,0,0.4)" }}>
              <img src={LOGO_URL} alt="" className="w-10 h-10 object-contain" />
              <div>
                <div className="brand-title text-base" style={{ color: "var(--p-burgundy)" }}>Psychelibrium</div>
                <div className="brand-sub mt-0.5" style={{ color: "var(--p-brown)" }}>Est. 2020</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="px-6 md:px-14 pb-20">
        <div className="rule-gold mb-14" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <Pillar
            Icon={User}
            title="Atención personalizada"
            body="Adaptamos cada proceso a tus necesidades y ritmo personal."
          />
          <Pillar
            Icon={Brain}
            title="Psicólogos especializados"
            body="Formación sólida y actualización constante en distintas áreas de la psicología."
          />
          <Pillar
            Icon={BookOpen}
            title="Enfoque basado en evidencia"
            body="Utilizamos herramientas y técnicas respaldadas por la investigación científica."
          />
          <Pillar
            Icon={PlayCircle}
            title="Modalidad online"
            body="Sesiones cómodas, accesibles y efectivas desde la seguridad de tu espacio."
          />
        </div>
      </section>

      {/* SECTIONS GRID, replaces carousel */}
      <section className="px-6 md:px-14 pb-24">
        <div className="mb-10">
          <p className="eyebrow" style={{ color: "var(--p-burgundy)" }}>
            Explora
          </p>
          <h2
            className="mt-3"
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)",
              fontWeight: 500,
              color: "var(--p-black)",
              letterSpacing: "-0.01em",
            }}
          >
            Un espacio, muchas puertas.
          </h2>
          <p className="mt-2 max-w-2xl" style={{ color: "var(--p-brown)", fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
            Cada apartado te lleva a un aspecto distinto de nuestra práctica.
            Elige por dónde quieres comenzar.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <SectionCard
            to="/nosotros"
            eyebrow="01"
            title="Nosotros"
            body="Quiénes somos, qué nos mueve y cómo entendemos el acompañamiento psicológico."
            image="https://images.unsplash.com/photo-1519452575417-564c1401ecc0?auto=format&fit=crop&w=1200&q=80"
            testid="section-nosotros"
          />
          <SectionCard
            to="/servicios"
            eyebrow="02"
            title="Servicios"
            body="Terapia individual, de pareja, familiar, adolescentes, adultos y comunidad LGBTQ+."
            image="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80"
            testid="section-servicios"
          />
          <SectionCard
            to="/especialistas"
            eyebrow="03"
            title="Especialistas"
            body="Psicólogos clínicos y forenses con formación de maestría y práctica activa."
            image={LOGO_URL}
            logoStyle
            testid="section-especialistas"
          />
          <SectionCard
            to="/enfoques"
            eyebrow="04"
            title="Enfoques Terapéuticos"
            body="TCC, DBT, sistémica, gestalt, humanista y mindfulness integrados con criterio clínico."
            image="https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=1200&q=80"
            testid="section-enfoques"
          />
          <SectionCard
            to="/colecciones"
            eyebrow="05"
            title="Publicaciones"
            body="Investigaciones y artículos revisados por pares publicados por Katia Marcin y Gerardo Abadía."
            image="https://images.unsplash.com/photo-1509266272358-7701da638078?auto=format&fit=crop&w=1200&q=80"
            testid="section-colecciones"
          />
          <SectionCard
            to="/psicologia-forense"
            eyebrow="06"
            title="Psicología Forense"
            body="Dictámenes, peritajes y metaperitajes con rigor metodológico e independencia."
            image="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=1200&q=80"
            testid="section-forense"
            dark
          />
        </div>
      </section>
    </div>
  );
};

const MicroBadge = ({ Icon, title, subtitle }) => (
  <div className="flex items-start gap-3">
    <div className="mt-1" style={{ color: "var(--p-burgundy)" }}>
      <Icon size={20} strokeWidth={1.4} />
    </div>
    <div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1rem", color: "var(--p-black)", fontWeight: 600 }}>
        {title}
      </div>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "0.95rem", color: "var(--p-brown)" }}>
        {subtitle}
      </div>
    </div>
  </div>
);

const Pillar = ({ Icon, title, body }) => (
  <div className="flex flex-col items-start gap-3">
    <div
      className="w-11 h-11 rounded-full flex items-center justify-center"
      style={{ background: "rgba(74,21,33,0.06)", color: "var(--p-burgundy)" }}
    >
      <Icon size={20} strokeWidth={1.4} />
    </div>
    <h3
      style={{
        fontFamily: "Cormorant Garamond, serif",
        fontSize: "1.15rem",
        fontWeight: 600,
        color: "var(--p-black)",
      }}
    >
      {title}
    </h3>
    <p style={{ fontFamily: "Cormorant Garamond, serif", color: "var(--p-brown)", lineHeight: 1.55 }}>
      {body}
    </p>
  </div>
);

const SectionCard = ({ to, eyebrow, title, body, image, testid, dark, logoStyle }) => (
  <Link
    to={to}
    data-testid={testid}
    className="card-soft group relative block overflow-hidden"
    style={dark ? { background: "#161617", borderColor: "rgba(184,151,90,0.35)" } : undefined}
  >
    <div
      className="aspect-[16/10] overflow-hidden flex items-center justify-center"
      style={
        logoStyle
          ? {
              background:
                "radial-gradient(circle at 50% 40%, #6a1e2c 0%, #4a1521 55%, #2f0d15 100%)",
            }
          : undefined
      }
    >
      <img
        src={image}
        alt={title}
        className={
          logoStyle
            ? "w-[42%] h-auto object-contain transition-transform duration-700 group-hover:scale-105"
            : "w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        }
        style={logoStyle ? { filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.35))" } : undefined}
      />
    </div>
    <div className="p-6">
      <div className="flex items-center justify-between">
        <span
          className="eyebrow"
          style={{ color: dark ? "#b8975a" : "var(--p-burgundy)" }}
        >
          {eyebrow} · Sección
        </span>
        <ArrowUpRight
          size={18}
          className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
          style={{ color: dark ? "#e2c890" : "var(--p-burgundy)" }}
        />
      </div>
      <h3
        className="mt-3"
        style={{
          fontFamily: "Cormorant Garamond, serif",
          fontSize: "1.55rem",
          fontWeight: 600,
          color: dark ? "#efe4c8" : "var(--p-black)",
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </h3>
      <p
        className="mt-2"
        style={{
          fontFamily: "Cormorant Garamond, serif",
          fontSize: "1rem",
          color: dark ? "rgba(239,228,200,0.75)" : "var(--p-brown)",
          lineHeight: 1.55,
        }}
      >
        {body}
      </p>
    </div>
  </Link>
);

export default Home;
